/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../../api/impl/serviceability/nls/StringResources',
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'react-dom',
	'../../../../lib/@waca/core-client/js/core-client/ui/KeyCodes'
], function(StringResources, React, UI_TOOLKIT, ReactDOM, KeyCodes){

	const {
		Dialog,
		ToggleSwitchSection,
		FlexItem,
		FlexLayout,
		TextArea,
		TruncatedText,
		Tabs,
		TabPanel,
		Link
	} = UI_TOOLKIT;

	const {
		useState,
		useEffect
	} = React;

	return class ServiceabilitySQLInfo{
		constructor(visApi, queryExecution) {
			this.visApi = visApi;
			this.queryExecution = queryExecution;
			this._widgetLastCognosQuery = undefined;
			this._widgetLastNativeQuery = undefined;
			this._widgetLastMDXQuery = undefined;
			this._shouldFetchQuery = false;
			this.el$ = undefined;
		}

		set widgetsLastCognosQuery (cognosQuery) {
			this._widgetLastCognosQuery = cognosQuery;
		}

		get widgetsLastCognosQuery() {
			return this._widgetLastCognosQuery;
		}

		set widgetsLastNativeQuery (nativeQuery) {
			this._widgetLastNativeQuery = nativeQuery;
		}

		get widgetsLastNativeQuery() {
			return this._widgetLastNativeQuery;
		}

		get widgetsLastMDXQuery() {
			return this._widgetLastMDXQuery;
		}

		set widgetsLastMDXQuery (MDXQuery) {
			this._widgetLastMDXQuery = MDXQuery;
		}

		get shouldFetchQuery() {
			return this._shouldFetchQuery;
		}

		set shouldFetchQuery(state) {
			this._shouldFetchQuery = state;
		}

		showQueryDialog() {
			this.el$ = document.createElement('div');
			this.el$.classList.add('sql-popover-container');
			document.body.appendChild(this.el$);
			const hasQueryInfo = this.widgetsLastCognosQuery || this.widgetsLastNativeQuery || this.widgetsLastMDXQuery;
			const Dialog = this.QueryDialog;
			const DialogBody = this.QueryDialogBody;
			ReactDOM.render(
				<Dialog
					hasQueryInfo={hasQueryInfo}
					onClose={(evt) => this.removeSQLDialog(evt)}
				>
					<DialogBody
						cognosSQL={this.widgetsLastCognosQuery}
						nativeSQL={this.widgetsLastNativeQuery}
						MDXQuery={this.widgetsLastMDXQuery}
					/>
				</Dialog>, this.el$);
		}

		removeSQLDialog(evt) {
			if(evt) {
				evt.stopPropagation();
			}
			if(this.el$) {
				ReactDOM.unmountComponentAtNode(this.el$);
				this.el$.remove();
				this.el$ = undefined;
			}
		}

		QueryDialogBody(props) {
			const DialogTextArea = value => {
				return (
					<TextArea
						placeholder={StringResources.get('interactToTriggerQuery')}
						disabled={false}
						rows={20}
						resize={false}
						value={value}
						style={{ width: '100%' }}
						className={'sql-textarea'}
					/>
				);
			};

			const ErrorDialogBody = () => {
				return (
					<div data-id="s12y-sqlinfo-error">{StringResources.get('queryInfoNotAvailable')}</div>
				);
			};

			const {
				cognosSQL,
				nativeSQL,
				MDXQuery
			} = props;

			let defaultSelectedTab;
			if(cognosSQL) {
				defaultSelectedTab = 'cognossql';
			} else if(nativeSQL) {
				defaultSelectedTab = 'nativesql';
			} else if(MDXQuery) {
				defaultSelectedTab = 'mdxquery';
			}
			const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);

			if (cognosSQL || nativeSQL || MDXQuery) {
				return (
					<Tabs
						onChange={tabId => setSelectedTab(tabId)}
						selected={selectedTab}
						vertical={true}
						linePosition={'right'}
					>
						{ cognosSQL && <TabPanel id={'cognossql'} label={'Cognos SQL'}>{ DialogTextArea(cognosSQL) }</TabPanel> }
						{ nativeSQL && <TabPanel id={'nativesql'} label={'Native SQL'}>{ DialogTextArea(nativeSQL) }</TabPanel> }
						{ MDXQuery && <TabPanel id={'mdxquery'} label={'MDX'}>{ DialogTextArea(MDXQuery) }</TabPanel> }
					</Tabs>
				);
			} else {
				return (
					<ErrorDialogBody />
				);
			}
		}

		QueryDialog(props) {
			const {
				onClose,
				hasQueryInfo,
				children
			} = props;

			return (
				<Dialog
					size={'large'}
					width='80%'
					className={'sql-textarea-dialog'}
				>
					<Dialog.Header>
						<TruncatedText value={StringResources.get('SQLMDX')} />
					</Dialog.Header>
					<Dialog.Body>
						<FlexLayout>
							<FlexItem overflow={'auto'} style={{ width: '100%' }} >
								{children}
							</FlexItem>
						</FlexLayout>
					</Dialog.Body>
					<Dialog.Footer>
						{hasQueryInfo && <Dialog.Button label={StringResources.get('copy')} primary className={'query-dialog-copy-button'} onClick={
							() => {
								document.body.querySelector('.sql-textarea textarea').select();
								document.execCommand('copy');
							}}
						/>}
						<Dialog.Button label={StringResources.get('close')} onClick={(evt) => onClose(evt)} className={'query-dialog-close-button'}/>
					</Dialog.Footer>
				</Dialog>
			);
		}

		QuerySection(props) {
			const {
				initialToggleState,
				onToggle,
				openDialog
			} = props;

			const [toggleState, setToggleState] = useState(initialToggleState);
			const [linkDisabled, setLinkDisabled] = useState(!initialToggleState);

			useEffect(() => {
				onToggle(toggleState, setLinkDisabled);
			});


			return (
				<FlexLayout direction={'column'}>
					<FlexItem>
						<ToggleSwitchSection
							label={StringResources.get('fetchQuery')}
							rtl={false}
							className={'showSQLInfoToggle'}
							checked={toggleState}
							small={true}
							onChange={() => setToggleState(!toggleState)}
						/>
					</FlexItem>
					<FlexItem
						tabIndex={0}
						onKeyDown={ServiceabilitySQLInfo.onKeyDown.bind(ServiceabilitySQLInfo, openDialog)}
					>
						<Link
							className={'showSQLMDXLink'}
							onClick={() => openDialog()}
							disabled={linkDisabled}>
							{StringResources.get('showSQLMDX')}
						</Link>
					</FlexItem>
				</FlexLayout>
			);
		}

		/**
		 * @returns {object} The result of this result should be an object which will
		 * be stringfied later.
		 * @description this method will fetch the last sql result from the renderSequence
		 */
		toJSON() {
			return {
				sqlQuery: this.widgetsLastCognosQuery
			};
		}

		/**
		 * @description this method returns a JSON representation of the query section in the serviceability tab
		 * @returns {object} The UI spec which will be consumed by ContentInfoAPI
		 */
		toUIJSON() {
			const SQL = this.QuerySection;

			const node = (
				<SQL
					initialToggleState={this.shouldFetchQuery}
					onToggle={(toggleState, setLinkDisabled) => {
						this.shouldFetchQuery = toggleState;
						if(!toggleState) {
							this.widgetsLastCognosQuery = null;
							this.widgetsLastNativeQuery = null;
							setLinkDisabled(true);
						} else {
							if(!this.widgetsLastNativeQuery && !this.widgetsLastCognosQuery) {

								const option = { qfbMode: 'validate' };
								this.queryExecution && this.queryExecution.addRequestOptions(option);
								this.visApi.reRender({ sameQueryValidateMode : true }).then(() => {
									this.queryExecution && this.queryExecution.removeRequestOptions(['qfbMode']);
									setLinkDisabled(false);
								});
							}
						}
					}}
					openDialog={() => this.showQueryDialog()}
				/>);

			return [{
				sectionName: StringResources.get('query'),
				sectionValues: [{
					fieldName: StringResources.get('queryInfo'),
					node,
					type: 'ReactNode'
				}],
				properties: {
					open: false
				}
			}];
		}

		static onKeyDown (openDialog, event) {
			if (event.keyCode === KeyCodes.ENTER || event.keyCode === KeyCodes.SPACE) {
				// Prevent browser's default action: space or enter key is not a click
				// otherwise, if preventDefault is not called, the spec first get loaded loaded then get deleted resulting the
				// boardModelDialog containing no spec
				event.preventDefault();
				openDialog();
			}
		}
	};
});
